import type {
  CampaignCreationSubmit,
  SponsoredBrandsCreationSubmit,
  SponsoredDisplayCreationSubmit,
} from "@adpilot/shared";
import { isPrivilegedRole } from "@adpilot/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { type FieldPath, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import {
  type CampaignType,
  getSponsoredBrandsEligibility,
  getSponsoredDisplayAvailability,
} from "@/components/campaign-creation/wizard/campaign-type-availability";
import { useCampaignCreationQueries } from "@/components/campaign-creation/wizard/use-campaign-creation-queries";
import {
  recoveryTargetForSubmitError,
  type SectionId,
  sectionForPath,
} from "@/components/campaign-creation/wizard/wizard-navigation";
import { fetchAmazonConnectUrl } from "@/lib/amazon-api";
import { getApiErrorCode, getApiErrorDetails, getApiErrorMessage } from "@/lib/api-error";
import { submitCampaignCreation } from "@/lib/campaign-creation-api";
import {
  type CampaignCreationInvalidProduct,
  clearLegacyCampaignCreationState,
  clearSponsoredDisplayDraft,
  parseCampaignCreationInvalidProducts,
  rememberCampaignRequestShop,
} from "@/lib/campaign-creation-session";
import {
  buildCampaignCreationSubmit,
  type CampaignCreationFormValues,
  createInitialCampaignForm,
} from "@/lib/campaign-creation-state";
import { emitCampaignCreationTelemetry } from "@/lib/campaign-creation-telemetry";
import { useAuthStore } from "@/stores/auth";

const passthroughFormSchema = z.custom<CampaignCreationFormValues>(
  (value) => value != null && typeof value === "object",
);

const newCampaignRoute = getRouteApi("/_authed/campaigns/new");

type CampaignCreationPayload =
  | CampaignCreationSubmit
  | SponsoredBrandsCreationSubmit
  | SponsoredDisplayCreationSubmit;

/**
 * Wizard state machine: Shop selection, step/section navigation, Shop-switch
 * guarding, validation hand-off and submission for every campaign type.
 */
export function useCampaignCreationWizard() {
  const { t } = useTranslation("campaignCreation");
  const navigate = useNavigate();
  // ADP-137: entry from a portfolio detail carries ?shopId=&portfolioId=.
  const entrySearch = newCampaignRoute.useSearch();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const setCurrentShop = useAuthStore((state) => state.setCurrentShop);
  const workspaceId = user?.activeWorkspaceId ?? null;
  const workspaceRole = user?.workspaces?.find((workspace) => workspace.id === workspaceId)?.role;
  const canWrite = isPrivilegedRole(workspaceRole);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedCampaignType, setSelectedCampaignType] = useState<CampaignType>("sp");
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string>(() => crypto.randomUUID());
  const [enterInput, setEnterInput] = useState("");
  const [negativeKeywordInput, setNegativeKeywordInput] = useState("");
  const [negativeProductInput, setNegativeProductInput] = useState("");
  const [invalidProducts, setInvalidProducts] = useState<CampaignCreationInvalidProduct[]>([]);
  const [productCatalogBlocked, setProductCatalogBlocked] = useState(true);
  const [activeSection, setActiveSection] = useState<SectionId>("campaign");
  const [pendingShopId, setPendingShopId] = useState<string | null>(null);
  const [sponsoredBrandsDirty, setSponsoredBrandsDirty] = useState(false);
  const [sponsoredDisplayDirty, setSponsoredDisplayDirty] = useState(false);
  const form = useForm<CampaignCreationFormValues>({
    resolver: zodResolver(passthroughFormSchema),
  });
  const {
    accounts,
    writableProfiles,
    capabilities,
    sponsoredBrandsEligibility,
    sponsoredDisplayCapabilities,
  } = useCampaignCreationQueries({ canWrite, workspaceId, selectedShopId });
  const submit = useMutation({
    mutationFn: ({ payload, shopId }: { payload: CampaignCreationPayload; shopId: string }) =>
      submitCampaignCreation(payload, shopId),
    onSuccess: (_status, { payload, shopId }) => {
      // The Sponsored Product payload predates the adProduct discriminator.
      if ("adProduct" in payload && payload.adProduct === "sd") {
        clearSponsoredDisplayDraft(sessionStorage, shopId);
      }
      void navigate({
        to: "/campaigns/new/$requestId",
        params: { requestId: payload.requestId },
      });
    },
    onError: async (error) => {
      const code = await getApiErrorCode(error);
      const rejectedProducts = parseCampaignCreationInvalidProducts(
        await getApiErrorDetails(error),
      );
      const recoveryTarget = recoveryTargetForSubmitError(code);

      if (recoveryTarget) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["amazon", "accounts", workspaceId] }),
          queryClient.invalidateQueries({
            queryKey: ["campaign-creation", "capabilities", selectedShopId],
          }),
        ]);
        setStep(recoveryTarget.step);
        setActiveSection(recoveryTarget.section);
        if (rejectedProducts.length > 0) setInvalidProducts(rejectedProducts);
      }
      toast.error(await getApiErrorMessage(error));
    },
  });

  useEffect(() => {
    clearLegacyCampaignCreationState(sessionStorage);
    emitCampaignCreationTelemetry({ name: "wizard_open", step: 1 });
  }, []);

  useEffect(() => {
    if (!capabilities.data || !selectedShopId || form.getValues("campaignName")) return;
    form.reset(createInitialCampaignForm(capabilities.data));
  }, [capabilities.data, form, selectedShopId]);

  // ADP-137: entry from a portfolio detail pre-selects that portfolio's shop.
  // Runs once when the writable profiles resolve — the wizard is still pristine
  // at that point, so no unsaved-changes guard is needed.
  const entryShopApplied = useRef(false);
  useEffect(() => {
    if (entryShopApplied.current || !entrySearch.shopId || !workspaceId) return;
    if (!writableProfiles.some((profile) => profile.shopId === entrySearch.shopId)) return;
    entryShopApplied.current = true;
    if (selectedShopId === entrySearch.shopId) return;
    setCurrentShop(entrySearch.shopId);
    setSelectedShopId(entrySearch.shopId);
  }, [entrySearch.shopId, workspaceId, writableProfiles, selectedShopId, setCurrentShop]);

  const hasUnsavedChanges =
    form.formState.isDirty ||
    sponsoredBrandsDirty ||
    sponsoredDisplayDirty ||
    [enterInput, negativeKeywordInput, negativeProductInput].some(
      (input) => input.trim().length > 0,
    );

  useEffect(() => {
    if (!selectedShopId || !currentShopId || currentShopId === selectedShopId) return;
    if (hasUnsavedChanges) {
      setPendingShopId(currentShopId);
      setCurrentShop(selectedShopId);
      return;
    }
    if (workspaceId && writableProfiles.some((profile) => profile.shopId === currentShopId)) {
      setSelectedShopId(currentShopId);
      setRequestId(crypto.randomUUID());
      setEnterInput("");
      setNegativeKeywordInput("");
      setNegativeProductInput("");
      setInvalidProducts([]);
      setProductCatalogBlocked(true);
      setStep(1);
      form.reset();
      return;
    }
    setCurrentShop(selectedShopId);
  }, [
    currentShopId,
    form,
    hasUnsavedChanges,
    selectedShopId,
    setCurrentShop,
    workspaceId,
    writableProfiles,
  ]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const switchShop = (shopId: string) => {
    if (!workspaceId) return;
    setCurrentShop(shopId);
    setSelectedShopId(shopId);
    setRequestId(crypto.randomUUID());
    setEnterInput("");
    setNegativeKeywordInput("");
    setNegativeProductInput("");
    setInvalidProducts([]);
    setProductCatalogBlocked(true);
    setSponsoredBrandsDirty(false);
    setSponsoredDisplayDirty(false);
    setStep(1);
    form.reset();
    emitCampaignCreationTelemetry({ name: "profile_selected", step: 1, outcome: "connected" });
  };

  const selectShop = (shopId: string) => {
    if (selectedShopId && selectedShopId !== shopId && hasUnsavedChanges) setPendingShopId(shopId);
    else switchShop(shopId);
  };

  const confirmShopChange = () => {
    const next = pendingShopId;
    setPendingShopId(null);
    if (!next) return;
    // Only this confirmed branch promises the outgoing Shop's
    // bids and inputs "will be reset".
    if (selectedShopId) clearSponsoredDisplayDraft(sessionStorage, selectedShopId);
    switchShop(next);
  };

  const continueFromConfiguration = () => {
    if (!capabilities.data) return;
    if (invalidProducts.length > 0) {
      setActiveSection("products");
      document
        .getElementById("campaign-section-products")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      toast.error(t("validation.invalidProductsRemain"));
      return;
    }
    if (productCatalogBlocked) {
      setActiveSection("products");
      document
        .getElementById("campaign-section-products")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      toast.error(t("validation.catalogUnavailable"));
      return;
    }
    const pendingNegativeInput = negativeKeywordInput.trim()
      ? "negative-keyword-input"
      : negativeProductInput.trim()
        ? "negative-product-input"
        : null;
    if (pendingNegativeInput) {
      setActiveSection("negatives");
      document
        .getElementById("campaign-section-negatives")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => document.getElementById(pendingNegativeInput)?.focus());
      toast.error(t("validation.pendingNegativeInput"));
      return;
    }
    const result = buildCampaignCreationSubmit(requestId, form.getValues(), capabilities.data);
    if (!result.success) {
      const first = result.errors[0];
      const message = t(`validation.${first?.key ?? "invalid"}`, first ?? {});
      if (first) {
        form.setError(first.path as FieldPath<CampaignCreationFormValues>, {
          type: "manual",
          message,
        });
      }
      toast.error(message);
      const section = sectionForPath(first?.path ?? "");
      setActiveSection(section);
      document
        .getElementById(`campaign-section-${section}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      document.querySelector<HTMLElement>(`[name="${first?.path}"]`)?.focus();
      emitCampaignCreationTelemetry({
        name: "validation",
        step: 2,
        category: first?.key ?? "invalid",
      });
      return;
    }
    setStep(3);
    emitCampaignCreationTelemetry({ name: "step_navigation", step: 3, direction: "forward" });
  };

  const submitFromReview = () => {
    if (submit.isPending) return;
    if (!workspaceId || !selectedShopId || !capabilities.data) return;
    const result = buildCampaignCreationSubmit(requestId, form.getValues(), capabilities.data);
    if (!result.success) {
      setStep(2);
      continueFromConfiguration();
      return;
    }
    rememberCampaignRequestShop(sessionStorage, workspaceId, requestId, selectedShopId);
    emitCampaignCreationTelemetry({ name: "create_click", step: 3 });
    submit.mutate({ payload: result.data, shopId: selectedShopId });
  };

  /** Sponsored Brands / Display forms build their own payload and submit it directly. */
  const submitSponsoredCampaign = (
    payload: SponsoredBrandsCreationSubmit | SponsoredDisplayCreationSubmit,
    shopId: string,
  ) => {
    if (!workspaceId) return;
    rememberCampaignRequestShop(sessionStorage, workspaceId, payload.requestId, shopId);
    submit.mutate({ payload, shopId });
  };

  const reconnectAmazon = async () => {
    await queryClient.invalidateQueries({ queryKey: ["amazon", "accounts", workspaceId] });
    const { redirectUrl } = await fetchAmazonConnectUrl();
    window.location.href = redirectUrl;
  };

  const continueStepOne = () => {
    if (!selectedShopId) return;
    if (selectedCampaignType === "sb" && sponsoredBrandsEligibility.data !== true) return;
    if (
      selectedCampaignType === "sd" &&
      sponsoredDisplayCapabilities.data?.campaignCreation.state !== "supported"
    )
      return;
    setStep(2);
    emitCampaignCreationTelemetry({
      name: "step_navigation",
      step: 2,
      direction: "forward",
    });
  };

  const editFromReview = (targetStep: 1 | 2, section?: SectionId) => {
    setStep(targetStep);
    if (section) {
      setActiveSection(section);
      window.setTimeout(() => document.getElementById(`campaign-section-${section}`)?.focus(), 0);
    }
    emitCampaignCreationTelemetry({
      name: "step_navigation",
      step: targetStep,
      direction: "edit",
    });
  };

  const discardCampaign = () => {
    clearLegacyCampaignCreationState(sessionStorage);
    if (selectedShopId) clearSponsoredDisplayDraft(sessionStorage, selectedShopId);
    emitCampaignCreationTelemetry({ name: "cancel", step });
    setSelectedShopId(null);
    setRequestId(crypto.randomUUID());
    setEnterInput("");
    setNegativeKeywordInput("");
    setNegativeProductInput("");
    setInvalidProducts([]);
    setProductCatalogBlocked(true);
    setSponsoredBrandsDirty(false);
    setSponsoredDisplayDirty(false);
    setActiveSection("campaign");
    setPendingShopId(null);
    setStep(1);
    form.reset();
    void navigate({ to: "/campaigns" });
  };

  return {
    canWrite,
    backToCampaigns: () => void navigate({ to: "/campaigns" }),
    initialPortfolioId: entrySearch.portfolioId ?? null,
    step,
    setStep,
    activeSection,
    selectedCampaignType,
    setSelectedCampaignType,
    selectedShopId,
    requestId,
    form,
    enterInput,
    setEnterInput,
    negativeKeywordInput,
    setNegativeKeywordInput,
    negativeProductInput,
    setNegativeProductInput,
    invalidProducts,
    setInvalidProducts,
    setProductCatalogBlocked,
    setSponsoredBrandsDirty,
    setSponsoredDisplayDirty,
    accounts,
    writableProfiles,
    capabilities,
    sponsoredBrandsEligibility: getSponsoredBrandsEligibility(
      selectedShopId,
      sponsoredBrandsEligibility,
    ),
    sponsoredDisplayCapabilities,
    sponsoredDisplayAvailability: getSponsoredDisplayAvailability(
      selectedShopId,
      sponsoredDisplayCapabilities,
    ),
    isSubmitting: submit.isPending,
    pendingShopId,
    cancelShopChange: () => setPendingShopId(null),
    confirmShopChange,
    selectShop,
    reconnectAmazon,
    continueStepOne,
    continueFromConfiguration,
    submitFromReview,
    submitSponsoredCampaign,
    editFromReview,
    discardCampaign,
  };
}
